"use client";

import React from "react";
import { useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import * as CountryFlags from "country-flag-icons/react/3x2";

interface RegionMismatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameName?: string;
  gameSlug?: string;
  playerId?: string;
  detectedRegionCode?: string;
  detectedRegionName?: string;
  currentRegionCode?: string;
  currentRegionName?: string;
  targetUrl: string;
  actionButtonText?: string;
}

export default function RegionMismatchModal({
  isOpen,
  onClose,
  gameName = "Free Fire",
  gameSlug = "free-fire",
  playerId,
  detectedRegionCode = "ID",
  detectedRegionName = "Indonesia (ID)",
  currentRegionCode = "SG",
  currentRegionName = "Singapore / Malaysia (SG/MY)",
  targetUrl,
  actionButtonText,
}: RegionMismatchModalProps) {
  const router = useRouter();

  // If detected region matches current page, do not display mismatch modal
  const normDetected = (detectedRegionCode || "").toUpperCase();
  const normCurrent = (currentRegionCode || "").toUpperCase();
  if (!isOpen || (normDetected && normCurrent && normDetected === normCurrent)) {
    return null;
  }

  const handleRedirect = () => {
    if (playerId) {
      try {
        sessionStorage.setItem("topupbot_prefill_uid", playerId);
      } catch {}
    }
    onClose();
    router.push(targetUrl);
  };

  const FlagDetected =
    CountryFlags[normDetected as keyof typeof CountryFlags] || CountryFlags["ID"];
  const FlagCurrent =
    CountryFlags[normCurrent as keyof typeof CountryFlags] || CountryFlags["SG"];

  const buttonLabel =
    actionButtonText ||
    `Switch to ${gameName} ${detectedRegionName} Top Up`;

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 z-[99999] flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="region-mismatch-title"
      >
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        {/* Modal Card - Styled to match Baasil landing page & verify modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          className="relative bg-white border border-[#DBEDFF] rounded-xl shadow-2xl max-w-md w-full overflow-hidden text-center z-10"
          style={{ fontFamily: "var(--font-sans), sans-serif" }}
        >
          {/* Blue accent top bar */}
          <div className="h-1 w-full bg-gradient-to-r from-[#5A98D8] to-[#3b75b5]" />

          <div className="p-6 md:p-8">
            {/* Close Button */}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close modal"
              className="absolute right-4 top-4 p-1.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header Icon */}
            <div className="mb-4 text-center">
              <div className="w-12 h-12 rounded-full bg-[#EBF4FF] border border-[#DBEDFF] flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="w-6 h-6 text-[#1a5b9b]" />
              </div>
              <div className="inline-flex items-center gap-1.5 rounded-md bg-[#EBF4FF] border border-[#DBEDFF] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-[#1a5b9b] mb-2">
                Region Mismatch Detected
              </div>
              <h3
                id="region-mismatch-title"
                className="text-xl md:text-2xl font-bold uppercase tracking-wider text-[#1a5b9b] font-display"
              >
                Region is {detectedRegionName}
              </h3>
              <p className="text-xs text-slate-500 uppercase tracking-widest mt-1">
                Account belongs to {detectedRegionName} server
              </p>
            </div>

            {/* Description */}
            <p className="text-xs md:text-sm text-slate-600 leading-relaxed font-sans mb-5">
              {playerId ? (
                <>
                  Player ID{" "}
                  <span className="font-mono font-bold text-slate-800 bg-[#EBF4FF] border border-[#DBEDFF] px-2 py-0.5 rounded text-xs">
                    {playerId}
                  </span>{" "}
                  belongs to the{" "}
                </>
              ) : (
                "Your account belongs to the "
              )}
              <strong className="text-slate-900">{detectedRegionName}</strong> server.
              Top-ups purchased on {currentRegionName} will{" "}
              <span className="text-red-600 font-semibold">fail to deliver</span> to this account.
            </p>

            {/* Server Comparison Grid */}
            <div className="grid grid-cols-2 gap-2.5 text-left mb-6">
              {/* Current Page */}
              <div className="flex flex-col justify-between p-3 rounded-lg border border-red-200 bg-red-50/60">
                <div>
                  <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-red-600 mb-1">
                    <XCircle className="w-3.5 h-3.5 shrink-0" />
                    <span>Current Page</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1">
                    {FlagCurrent && (
                      <FlagCurrent className="w-4 h-3 object-cover rounded-sm shadow-xs shrink-0" />
                    )}
                    <span className="text-xs font-bold text-slate-800 truncate">
                      {currentRegionName}
                    </span>
                  </div>
                </div>
                <div className="mt-2">
                  <span className="inline-block rounded bg-red-100 text-red-700 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider">
                    Delivery Fails
                  </span>
                </div>
              </div>

              {/* Correct Region */}
              <div className="flex flex-col justify-between p-3 rounded-lg border border-emerald-200 bg-emerald-50/60">
                <div>
                  <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 mb-1">
                    <CheckCircle2 className="w-3.5 h-3.5 shrink-0 text-emerald-600" />
                    <span>Correct Region</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1">
                    {FlagDetected && (
                      <FlagDetected className="w-4 h-3 object-cover rounded-sm shadow-xs shrink-0" />
                    )}
                    <span className="text-xs font-bold text-slate-800 truncate">
                      {detectedRegionName}
                    </span>
                  </div>
                </div>
                <div className="mt-2">
                  <span className="inline-block rounded bg-emerald-100 text-emerald-700 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider">
                    Instant Delivery
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2.5">
              <button
                type="button"
                onClick={handleRedirect}
                className="w-full flex items-center justify-center gap-2 bg-[#5A98D8] hover:bg-[#4a88c8] text-white text-xs font-bold uppercase tracking-widest py-3.5 px-4 rounded-lg transition-all active:scale-[0.98] shadow-md hover:shadow-lg cursor-pointer"
              >
                <span>{buttonLabel}</span>
                <ArrowRight className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={onClose}
                className="w-full text-[10px] md:text-[11px] font-bold uppercase tracking-widest text-slate-400 hover:text-slate-600 transition-colors py-1.5 cursor-pointer"
              >
                Stay on current page / Dismiss
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
